import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@vision/ui/store';
import { SurveyLogicScope, SurveyLogicLegacyTrigger } from '@vision/ui/interfaces';

export const surveyLogicBuilderStatusSelector = createSelector(
  (state: RootState) => state.SurveyLogicBuilderState,
  ({ surveyLogicBuilderStatus }) => ({
    surveyLogicBuilderStatus,
  }),
);

export const surveyLogicBuilderSelector = createSelector(
  (state: RootState) => state.SurveyLogicBuilderState,
  (state) => ({
    activeFlowId: state.activeFlowId,
    surveyStates: state.surveyStates,
    rules: state.rules,
    editingRuleId: state.editingRuleId,
    activeLanguageCode: state.activeLanguageCode,
    isDirty: state.isDirty,
    validationErrors: state.validationErrors,
  }),
);

export const surveyLogicBuilderRulesSelector = (state: RootState) => state.SurveyLogicBuilderState.rules;
export const surveyLogicBuilderExpandedRuleIdSelector = (state: RootState) =>
  state.SurveyLogicBuilderState.expandedRuleId;
export const surveyLogicBuilderActiveLanguageCodeSelector = (state: RootState) =>
  state.SurveyLogicBuilderState.activeLanguageCode;
export const surveyLogicBuilderIsDirtySelector = (state: RootState) => state.SurveyLogicBuilderState.isDirty;
export const surveyLogicBuilderActiveFlowIdSelector = (state: RootState) => state.SurveyLogicBuilderState.activeFlowId;
export const surveyLogicBuilderValidationErrorsSelector = (state: RootState) =>
  state.SurveyLogicBuilderState.validationErrors;
export const surveyLogicBuilderSurveyStatesSelector = (state: RootState) => state.SurveyLogicBuilderState.surveyStates;

export const surveyLogicBuilderEditingRuleSelector = createSelector(
  [(state: RootState) => state.SurveyLogicBuilderState.editingRuleId, surveyLogicBuilderRulesSelector],
  (editingRuleId, rules) => {
    if (!editingRuleId) return null;
    return rules.find((rule) => rule.id === editingRuleId) || null;
  },
);

export const surveyLogicBuilderAllElementsSelector = createSelector(
  [surveyLogicBuilderSurveyStatesSelector],
  (surveyStates) => (surveyStates || []).flatMap((s) => s.elements || []),
);

export const surveyLogicBuilderOriginalConfigSelector = createSelector(
  (state: RootState) => state.SurveyLogicBuilderState,
  (state) => state.originalConditionRulesConfig,
);

export const surveyLogicBuilderTriggersMapSelector = createSelector(
  surveyLogicBuilderSurveyStatesSelector,
  (states) => {
    const triggersMap = new Map<string, Array<{ trigger: SurveyLogicLegacyTrigger; index: number }>>();

    (states || []).forEach((state) => {
      (state.elements || []).forEach((element) => {
        if (element.triggers && element.triggers.length > 0) {
          triggersMap.set(
            element.key,
            element.triggers.map((trigger, index) => ({ trigger, index })),
          );
        }
      });
    });

    return triggersMap;
  },
);

export const surveyLogicBuilderRulesSummarySelector = createSelector(surveyLogicBuilderRulesSelector, (rules) =>
  (rules || []).map((rule, index) => ({
    id: rule.id,
    name: rule.name || `Rule ${index + 1}`,
    conditionCount: (rule.conditionGroups || []).reduce((sum, group) => sum + (group.conditions || []).length, 0),
    groupCount: (rule.conditionGroups || []).length,
    actionType: rule.action?.type,
    actionScope: rule.action?.scope,
    targetCount:
      rule.action?.scope === SurveyLogicScope.QUESTION
        ? (rule.action?.targetElementKeys || []).length
        : rule.action?.scope === SurveyLogicScope.OPTION
          ? (rule.action?.targetOptionKeys || []).length
          : (rule.action?.targetEvents || []).length,
  })),
);
